import React, { useMemo, useState } from 'react'
import { Grid } from '@mui/material'
import { MailOutlineRounded } from '@mui/icons-material'
import { useTranslation } from 'react-i18next'
import './ContactUs.css'

const CONTACT_FORM_URL = 'https://us-central1-tiim-f0972.cloudfunctions.net/email-contactForm'

const ContactUs = () => {
  const { t, i18n } = useTranslation()
  const subjectOptions = useMemo(() => t('contactUs.form.subjectOptions', { returnObjects: true }) || [], [t])

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: '',
  })
  const [submitState, setSubmitState] = useState({
    status: 'idle',
    message: '',
  })

  const isSubmitting = submitState.status === 'loading'

  const onChangeField = (field) => (event) => {
    setFormData((prev) => ({ ...prev, [field]: event.target.value }))

    if (submitState.status !== 'idle') {
      setSubmitState({ status: 'idle', message: '' })
    }
  }

  const onSubmit = async (event) => {
    event.preventDefault()

    const payload = {
      name: formData.name.trim(),
      email: formData.email.trim(),
      subject: formData.subject.trim(),
      message: formData.message.trim(),
      language: i18n.language,
      source: 'tiimschool-landing',
    }

    if (!payload.name || !payload.email || !payload.subject || !payload.message) {
      setSubmitState({
        status: 'error',
        message: t('contactUs.form.validationError', { defaultValue: 'Please complete all required fields.' }),
      })
      return
    }

    try {
      setSubmitState({
        status: 'loading',
        message: t('contactUs.form.sending', { defaultValue: 'Sending message...' }),
      })

      const response = await fetch(CONTACT_FORM_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      })

      if (!response.ok) {
        throw new Error(`Request failed with status ${response.status}`)
      }

      setSubmitState({
        status: 'success',
        message: t('contactUs.form.success', { defaultValue: 'Message sent successfully. We will contact you soon.' }),
      })

      setFormData({
        name: '',
        email: '',
        subject: '',
        message: '',
      })
    } catch (error) {
      setSubmitState({
        status: 'error',
        message: t('contactUs.form.error', { defaultValue: 'We could not send your message right now. Please try again in a moment.' }),
      })
    }
  }

  return (
    <section className='contactUs' id='contact' style={{ scrollMarginTop: 'clamp(96px, 10vw, 212px)' }}>
      <Grid container className='body'>
        <Grid container item xs={12} md={5} className='left' flexDirection={'column'}>
          <label>{t('contactUs.label')}</label>
          <h1>{t('contactUs.title')}</h1>

          <p>{t('contactUs.description1')}</p>
          <p>{t('contactUs.description2')}</p>

          <div className='emailRow'>
            <div className='iconWrap'>
              <MailOutlineRounded className='mailIcon' />
            </div>
            <div>
              <h4>{t('contactUs.emailLabel')}</h4>
              <a href='mailto:hello@tiimapp.com'>{t('contactUs.email')}</a>
            </div>
          </div>

          <button type='button' className='demoButton' onClick={() => window.location.href = "mailto:hello@tiimapp.com"}>
            {t('contactUs.demo')}
          </button>
        </Grid>

        <Grid container item xs={12} md={7} className='right'>
          <form className='formCard' onSubmit={onSubmit}>
            <label htmlFor='contact-name'>{t('contactUs.form.name')}</label>
            <input
              id='contact-name'
              type='text'
              required
              value={formData.name}
              onChange={onChangeField('name')}
              disabled={isSubmitting}
            />

            <label htmlFor='contact-email'>{t('contactUs.form.email')}</label>
            <input
              id='contact-email'
              type='email'
              required
              value={formData.email}
              onChange={onChangeField('email')}
              disabled={isSubmitting}
            />

            <label htmlFor='contact-subject'>{t('contactUs.form.subject')}</label>
            <select
              id='contact-subject'
              required
              value={formData.subject}
              onChange={onChangeField('subject')}
              disabled={isSubmitting}
            >
              <option value='' disabled>{t('contactUs.form.select')}</option>
              {subjectOptions.map((item, index) => (
                <option value={item} key={index}>{item}</option>
              ))}
            </select>

            <label htmlFor='contact-message'>{t('contactUs.form.message')}</label>
            <textarea
              id='contact-message'
              rows={5}
              required
              value={formData.message}
              onChange={onChangeField('message')}
              disabled={isSubmitting}
            />

            <button className='submitButton' type='submit' disabled={isSubmitting}>
              {isSubmitting
                ? t('contactUs.form.sending', { defaultValue: 'Sending message...' })
                : t('contactUs.form.submit')}
            </button>

            {submitState.message && (
              <p className={`submitFeedback ${submitState.status}`}>{submitState.message}</p>
            )}
          </form>
        </Grid>
      </Grid>
    </section>
  )
}

export default ContactUs